/*    
<summary>
  This functional component "AddOptionMenu" defines the props like initial values and functions for the child form component.
  Developer:Sahil Bharti, Created Date:06-January-2025
</summary>
<returns>Returns JSX</returns>
*/

import { cloneDeep } from "lodash";
import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { ActionType } from "../../../constants/enums/action-type-enum";
import { ModuleType } from "../../../constants/enums/module-type-enum";
import userTypeEnum from "../../../constants/enums/user-type-enum";
import { registrationTypeOptions } from "../../../constants/options.constant";
import { useStore } from "../../../contexts/store-provider";
import { isOperationPermittedRoleBase } from "../../../helpers/action-permission-helper";
import {
  getStoreIdFromLocalStorage,
  getUserTypeFromLocalStorage,
} from "../../../helpers/localstorage-helper";
import { IAddCustomer } from "../../../models/forms/IAddCustomerManagement";
import { formatMessage } from "../../../translations/format-message";
import AddEditCustomerManagementForm from "../forms/add-edit-customer-management-form";
import RoutesConstants from "../../routes.constant";
import ConfirmPopup from "../../../shared-components/popup/confirm/confirm-popup";
import ConfirmPopupHOC from "../../../shared-components/popup/confirm/confirm-popup-hoc";
// import AdvanceSearchModal from "../forms/advance-search-form";

interface IProps {
  id: number;
  viewOnly: boolean;
  modalClosed: () => void;
  routeFrom?: string;
  showConfirmPopup?: boolean;
  confirmPopupToggleHandler: () => void;
}

const AddEditCustomerManagement = (props: IProps) => {
  const {
    masterManagementStore,
    treatmentManagementStore,
    customerManagementStore,
    storeManagementStore,
    smrStore,
  } = useStore();
  const {
    inProgress,
    GetCustomerByIdService,
    resetAddUpdateCustomerState,
    resetCustomerDetail,
    customerState,
    customerDetail,
    addUpdateCustomerState,
    AddCustomerService,
    UpdateCustomerService,
    deleteCustomerState,
    DeleteCustomerService,
    GetAllCustomerListService,
    getAllCustomer,
    allCustomerState,
    resetAllCustomerList,
    resetDeleteCustomer,
  } = customerManagementStore;
  const { storeState, resetPrefectureByZipCode } = storeManagementStore;

  const { getAllTreatmentOptionsAll } = treatmentManagementStore;
  const {
    getAllTaxOptions,
    getAllTimeOptions,
    GetAllMotivationService,
    getAllMotivationOptions,
    GetStoreMotivationByStoreId,
    getMotivationInStoreOptions,
  } = masterManagementStore;

  const initialValues: IAddCustomer = customerDetail;
  const storeId = getStoreIdFromLocalStorage();
  const userType = getUserTypeFromLocalStorage();
  const [values, setTempValues] = useState<any>(null);
  /**
   * The useEffect Hook lets you perform side effects in function component.
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered
   * while a dependency of the useEffect has changed.
   */
  useEffect(() => {
    if (userType === userTypeEnum[userTypeEnum.Store]) {
      if (storeId > 0) {
        GetStoreMotivationByStoreId(storeId);
      }
    } else {
      GetAllMotivationService();
    }
    GetAllCustomerListService();

    if (props.id > 0) {
      let userTypeFromLocalStorage = getUserTypeFromLocalStorage();
      GetCustomerByIdService(
        props.id,
        userTypeFromLocalStorage !== userTypeEnum[userTypeEnum.SuperAdmin] &&
          userTypeFromLocalStorage !== userTypeEnum[userTypeEnum.HeadQuarter]
          ? getStoreIdFromLocalStorage()
          : null
      );
    }
  }, []);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "addUpdateCustomerSuccess" of the useEffect changed.
   */


  useEffect(() => {
    if (addUpdateCustomerState.success) {
      setTempValues(null);
      if (props.showConfirmPopup) {
        props.confirmPopupToggleHandler();
      }
      if (props.id === -1) toast.success(formatMessage("added_success"));
      else toast.success(formatMessage("updated_success"));
      onModalClosed();
    }
  }, [addUpdateCustomerState.success]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "customerDetailError" of the useEffect changed.
   */
  useEffect(() => {
    if (customerState.error) {
      toast.error(formatMessage(customerState.error));
    }
  }, [customerState.error]);


  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "addUpdateCustomerError" of the useEffect changed.
   */
  useEffect(() => {
    if (addUpdateCustomerState.error) {
      if (
        addUpdateCustomerState.error == "IsThereADifferentEmailIdForThisAccount"
      ) {
        props.confirmPopupToggleHandler();
      } else {
        toast.error(formatMessage(addUpdateCustomerState.error));
      }
      resetAddUpdateCustomerState();
    }
  }, [addUpdateCustomerState.error]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "deleteCustomerSuccess" of the useEffect changed.
   */
  useEffect(() => {
    if (deleteCustomerState.success) {
      toast.success(formatMessage("deleted_success"));
      onDeleteModalClosed();
    }
  }, [deleteCustomerState.success]);

  /**
   * This function adds the customer when "Id < 0" and updates the Existing customer when Id > 0 by providing form values to the
   * correponding function
   */
  const onSubmitForm = (values: any) => {
    let tempValues = cloneDeep(values);
    setTempValues(tempValues);
    if (props.id < 0) AddCustomerService(tempValues);
    else UpdateCustomerService(props.id, tempValues);
  };

  const onSubmitOverwriteForm = () => {
    let tempValues = cloneDeep(values);
    if (props.id < 0) AddCustomerService({ ...tempValues, IsOverwriteEmail: true });
    else UpdateCustomerService(props.id, tempValues);
  };

  /**
   * This function closes the pop up modal and reset getcustomerdetails, addUpdate treatement observables (success, inprogress
   * and error).
   */
  const onModalClosed = () => {
    if (props.routeFrom !== RoutesConstants.SalesMedicalRecords) {
      resetAllCustomerList();
      resetCustomerDetail();
    }
    resetPrefectureByZipCode();
    props.modalClosed();
  };

  const handleDeleteSubmitHandler = () => {
    DeleteCustomerService(props.id);
  };

  /**
   * This function closes the pop up modal and reset getcustomerdetails, delete customer observables (success, inprogress
   * and error).
   */
  const onDeleteModalClosed = () => {
    resetCustomerDetail();
    props.modalClosed();
  };

  useEffect(() => {
    if (deleteCustomerState.error) {
      onDeleteModalClosed();
      resetDeleteCustomer();
    }
  }, [deleteCustomerState.error, onDeleteModalClosed, resetDeleteCustomer]);

  return (
    <React.Fragment>

      {props.showConfirmPopup && (
        <ConfirmPopup
          isLoading={false}
          title={formatMessage("confirmation_popup_title")}
          message={formatMessage("IsThereADifferentEmailIdForThisAccountInfo")}
          modalSubmit={onSubmitOverwriteForm}
          modalClosed={props.confirmPopupToggleHandler}
        />
      )}


      <AddEditCustomerManagementForm
        initialValues={initialValues}
        id={props.id}
        timeOptions={getAllTimeOptions}
        taxOptions={getAllTaxOptions}
        treatmentOptions={getAllTreatmentOptionsAll}
        submitHandler={onSubmitForm}
        onModalClosed={onModalClosed}
        registrationTypeOptions={registrationTypeOptions}
        motivationOptions={
          userType === userTypeEnum[userTypeEnum.Store]
            ? getMotivationInStoreOptions
            : getAllMotivationOptions
        }
        getCustomerLoading={
          customerState.inProgress ||
          addUpdateCustomerState.inProgress ||
          storeState.inProgress ||
          allCustomerState.inProgress ||
          inProgress
        }
        handleDeleteSubmitHandler={handleDeleteSubmitHandler}
        deleteActionLoading={deleteCustomerState.inProgress}
        routeFrom={props.routeFrom}
        viewOnly={
          props.viewOnly
            ? props.viewOnly
            : props.id > 0
              ? !isOperationPermittedRoleBase(
                ModuleType.CustomerManagement,
                ActionType.Add
              )
              : false
        }
        allCustomerList={getAllCustomer}
      />
    </React.Fragment>
  );
};

export default ConfirmPopupHOC(observer(AddEditCustomerManagement));
